<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Alert from '@/components/v2/Alert.vue';
import SubscriptionRunBadge from '@/components/subscriptions/SubscriptionRunBadge.vue';
import { dashboardSubscriptionService } from '@/services/DashboardSubscriptionService';
import { SubscriptionRun } from '@/types/DashboardSubscription';
import { formatRunTime } from '@/utilities/SubscriptionSchedule';

const props = defineProps<{
  subscriptionId: number;
}>();

const loading = ref(true);
const loadError = ref<string>();
const runs = ref<SubscriptionRun[]>([]);

onMounted(async () => {
  try {
    runs.value = await dashboardSubscriptionService.runs(props.subscriptionId);
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Delivery history could not be loaded.';
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div
    class="rounded-lg border border-solid border-gray-200 bg-gray-50 p-4"
    data-testid="dashboard-subscription-history">
    <div
      v-if="loading"
      class="loading loading-sm loading-spinner"></div>
    <Alert
      v-else-if="loadError"
      type="error">
      {{ loadError }}
    </Alert>
    <p
      v-else-if="runs.length === 0"
      class="text-sm text-gray-500">
      Nothing has been sent yet.
    </p>
    <table
      v-else
      class="w-full overflow-hidden rounded-md bg-white text-sm">
      <thead>
        <tr class="bg-gray-100 text-xs tracking-wide text-gray-500 uppercase">
          <th class="px-3 py-2 text-left font-semibold">Sent</th>
          <th class="px-3 py-2 text-left font-semibold">Result</th>
          <th class="px-3 py-2 text-left font-semibold">Reason</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100">
        <tr
          v-for="run in runs"
          :key="run.id">
          <td class="px-3 py-2">
            <span class="font-mono text-xs">{{ formatRunTime(run) }}</span>
            <span
              v-if="run.trigger === 'MANUAL'"
              data-testid="run-manual"
              class="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-800">
              Sent now
            </span>
          </td>
          <td class="px-3 py-2"><SubscriptionRunBadge :result="run.result" /></td>
          <td class="px-3 py-2 text-gray-600">{{ run.message || '—' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
